import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { studentGuardiansApi } from '@/services/api/endpoints/guardians';
import type { LinkGuardianPayload } from '@/services/api/endpoints/guardians';
import { LinkGuardianModal } from './LinkGuardianModal';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { StudentGuardian } from '@/types/domain';
import { humanise } from '@/utils/format';

/** The badges a link carries, rendered only when granted. */
function Authorisations({ link }: { link: StudentGuardian }) {
  const granted = [
    link.primaryGuardian && { label: 'Primary', tone: 'accent' as const },
    link.emergencyContact && { label: 'Emergency contact', tone: 'neutral' as const },
    link.pickupAuthorization && { label: 'May collect', tone: 'neutral' as const },
    link.financialResponsibility && { label: 'Receives invoices', tone: 'neutral' as const },
    link.livesWithStudent && { label: 'Lives with child', tone: 'neutral' as const },
  ].filter((entry): entry is { label: string; tone: 'accent' | 'neutral' } => entry !== false);

  return (
    <div className="flex flex-wrap gap-1.5">
      {granted.map((entry) => (
        <Badge key={entry.label} tone={entry.tone}>
          {entry.label}
        </Badge>
      ))}
    </div>
  );
}

/**
 * A child's guardians.
 *
 * Unlinking removes the relationship, never the person: the same guardian is
 * very likely a sibling's primary contact, and deleting them because one child
 * left would strip that child's details with no warning.
 */
export function StudentGuardiansPanel({ studentId }: { studentId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [unlinking, setUnlinking] = useState<StudentGuardian | null>(null);
  const [linking, setLinking] = useState(false);
  const [editingLink, setEditingLink] = useState<StudentGuardian | null>(null);

  const query = useQuery({
    queryKey: queryKeys.studentProfile.guardians(studentId),
    queryFn: () => studentGuardiansApi.list(studentId),
    enabled: can(PERMISSIONS.GUARDIAN_VIEW),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.studentProfile.guardians(studentId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.guardians.all });
  };

  const link = useMutation({
    mutationFn: (payload: LinkGuardianPayload) => studentGuardiansApi.link(studentId, payload),
    onSuccess: () => {
      toast.success('Guardian linked');
      setLinking(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const updateLink = useMutation({
    mutationFn: (payload: LinkGuardianPayload) =>
      studentGuardiansApi.update(studentId, editingLink?.guardianId ?? '', payload),
    onSuccess: () => {
      toast.success('Guardian role updated');
      setEditingLink(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const unlink = useMutation({
    mutationFn: (guardianId: string) => studentGuardiansApi.unlink(studentId, guardianId),
    onSuccess: () => {
      toast.success('Guardian unlinked. Their record is unchanged.');
      setUnlinking(null);
      invalidate();
    },
    onError: (error) => {
      handleApiError(error);
      setUnlinking(null);
    },
  });

  if (!can(PERMISSIONS.GUARDIAN_VIEW)) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Guardians"
          description="Shared records — editing a guardian changes their details for every child they are linked to."
          action={
            can(PERMISSIONS.GUARDIAN_ASSIGN) ? (
              <Button size="sm" variant="secondary" onClick={() => setLinking(true)}>
                Link guardian
              </Button>
            ) : undefined
          }
        />

        {query.isPending ? (
          <LoadingState label="Loading guardians" />
        ) : query.error !== null ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data === undefined || query.data.length === 0 ? (
          <p className="py-6 text-center text-sm text-subtle">
            No guardians linked yet. A child should have at least one contactable adult on record.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {query.data.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-3">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={ROUTES.guardianDetail(entry.guardianId)}
                      className="text-sm font-medium text-ink hover:text-accent"
                    >
                      {entry.guardianName}
                    </Link>
                    <span className="text-xs text-subtle">{humanise(entry.relationshipType)}</span>
                  </div>
                  <p className="font-mono text-xs text-muted">{entry.guardianPhone}</p>
                  <Authorisations link={entry} />
                  {entry.notes !== undefined && <p className="text-xs text-subtle">{entry.notes}</p>}
                </div>

                {can(PERMISSIONS.GUARDIAN_ASSIGN) && (
                  <div className="flex shrink-0 gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditingLink(entry)}>
                      Edit role
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setUnlinking(entry)}>
                      Unlink
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <LinkGuardianModal
        open={linking}
        saving={link.isPending}
        onClose={() => setLinking(false)}
        onSubmit={(payload) => link.mutate(payload)}
      />

      {editingLink !== null && (
        <LinkGuardianModal
          open
          link={editingLink}
          saving={updateLink.isPending}
          onClose={() => setEditingLink(null)}
          onSubmit={(payload) => updateLink.mutate(payload)}
        />
      )}

      <ConfirmDialog
        open={unlinking !== null}
        title="Unlink this guardian?"
        // Says plainly what is and is not removed, because "delete" on a shared
        // record is exactly where people expect the worst.
        message={`${unlinking?.guardianName ?? ''} will no longer be linked to this student. Their guardian record is kept, along with any links to their other children.`}
        confirmLabel="Unlink"
        loading={unlink.isPending}
        onCancel={() => setUnlinking(null)}
        onConfirm={() => {
          if (unlinking !== null) {
            unlink.mutate(unlinking.guardianId);
          }
        }}
      />
    </>
  );
}
